import React from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { useRole, type UserRole } from '@/hooks/useRole'
import { Spinner } from '@/design-system/Spinner'

/* ------------------------------------------------------------------ */
/*  RequireRole — role-based route guard                               */
/*  Redirects role mismatch to /                                       */
/*  Optional requireBankId for admin routes (stub — Phase 5 wires up)  */
/*  See ARCHITECTURE.md §5                                             */
/* ------------------------------------------------------------------ */

export interface RequireRoleProps {
  /** Required role to access the route */
  role: UserRole | UserRole[]
  /** If true, also requires BankID verification (Phase 5) */
  requireBankId?: boolean
}

export function RequireRole({ role, requireBankId: _requireBankId }: RequireRoleProps) {
  const { role: userRole, isLoading, isError } = useRole()

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size={24} />
      </div>
    )
  }

  if (isError || !userRole) {
    return <Navigate to="/" replace />
  }

  const allowedRoles = Array.isArray(role) ? role : [role]
  if (!allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />
  }

  // BankID verification check — will be wired in Phase 5 via Criipto service
  // For now, the stub passes through if the role matches

  return <Outlet />
}

RequireRole.displayName = 'RequireRole'
